/* ==========================================================================
   Marquee block (_bc-marquee) — infinite horizontal scroller.

   Technique: the authored items live in a single .cuy-marquee__group. We clone
   that group enough times to overflow the viewport, then run one linear
   transform from 0 to -(one group + gap) via the Web Animations API. Because
   every group is identical, the end state is pixel-identical to the start, so
   the loop is seamless. Speed is pixels-per-second, so perceived speed is the
   same regardless of how many items are added.

   Sizing/measurement is recomputed on resize and after images load via a
   ResizeObserver on the original group, and the whole thing re-initialises on
   Shopify theme-editor section loads.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false, addEventListener: function () {} };

  // Every live Marquee on the page. Needed because the "control all marquees"
  // and "one button per page" settings are cross-instance.
  var INSTANCES = [];

  function liveInstances() {
    return INSTANCES.filter(function (m) {
      return m.root.isConnected;
    }).sort(function (a, b) {
      // Document order, which is what "the first marquee" means to a merchant.
      return a.root.compareDocumentPosition(b.root) & Node.DOCUMENT_POSITION_FOLLOWING
        ? -1
        : 1;
    });
  }

  // "Show only one pause button per page": the first marquee that is actually
  // visible at this breakpoint keeps its button and drives every marquee; the
  // rest have theirs hidden. Picking the first VISIBLE one matters because the
  // Visibility setting can display:none whichever marquee comes first in the
  // DOM, which would otherwise hide the page's only control.
  function applySingleButton() {
    var live = liveInstances();
    var wanted = live.some(function (m) {
      return m.singleButton;
    });

    live.forEach(function (m) {
      m.controlAll = m.controlAllSetting;
    });

    if (wanted) {
      var owner = null;
      live.forEach(function (m) {
        if (!owner && m.root.offsetParent !== null) owner = m;
      });
      if (!owner) owner = live[0];

      if (owner) {
        // The surviving button has to drive all of them: hiding the others
        // while leaving them independently animating would strand those with no
        // pause mechanism at all, which is the WCAG 2.2.2 failure this control
        // exists to fix. So ownership implies control-all, whatever that
        // block's own setting says.
        owner.controlAll = true;
      }

      live.forEach(function (m) {
        if (m.toggle) m.toggle.hidden = m !== owner;
      });
    } else {
      live.forEach(function (m) {
        if (m.toggle) m.toggle.hidden = false;
      });
    }

    // Re-sync every button last. Each instance sync-ed during its own
    // constructor, when later marquees had not registered yet, so a "controls
    // all" button built first would still be claiming the singular label.
    live.forEach(function (m) {
      m._sync();
    });
  }

  function waitForImages(el) {
    var imgs = Array.prototype.slice.call(el.querySelectorAll('img'));
    var pending = imgs.filter(function (img) {
      return !img.complete || img.naturalWidth === 0;
    });
    if (!pending.length) return Promise.resolve();
    return Promise.all(
      pending.map(function (img) {
        return new Promise(function (resolve) {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      })
    );
  }

  function Marquee(root) {
    this.root = root;
    this.viewport = root.querySelector('.cuy-marquee__viewport');
    this.track = root.querySelector('.cuy-marquee__track');
    this.original = root.querySelector('.cuy-marquee__group');
    this.toggle = root.querySelector('[data-cuy-marquee-toggle]');
    this.animation = null;
    this.resizeRaf = 0;
    this.hoverPaused = false; // true while hovered/focused (pause-on-hover)
    // null = the visitor has not touched the toggle, so pause-on-hover decides.
    // Once they do, their explicit choice wins for the life of the component.
    this.userPaused = null;
    this._lastVw = 0;
    this._lastGw = 0;
    this._onEnter = this._onEnter.bind(this);
    this._onLeave = this._onLeave.bind(this);
    this._onToggle = this._onToggle.bind(this);
    this._scheduleBuild = this._scheduleBuild.bind(this);

    if (!this.viewport || !this.track || !this.original) return;
    root.__cuyMarquee = this;

    this.direction = root.getAttribute('data-direction') === 'right' ? 'right' : 'left';
    this.speed = Math.max(1, parseFloat(root.getAttribute('data-speed')) || 60); // px/sec
    this.pauseOnHover = root.getAttribute('data-pause-hover') === 'true';
    this.singleButton = root.getAttribute('data-single-button') === 'true';
    // Declared value is kept separately: applySingleButton force-enables
    // controlAll on whichever marquee owns the page's button, and ownership can
    // move between breakpoints, so it needs something to reset back to.
    this.controlAllSetting =
      root.getAttribute('data-control-all') === 'true' || this.singleButton;
    this.controlAll = this.controlAllSetting;

    INSTANCES.push(this);

    if (this.toggle) {
      this.toggle.addEventListener('click', this._onToggle);
      this._sync();
    }

    if (this.pauseOnHover) {
      root.addEventListener('mouseenter', this._onEnter);
      root.addEventListener('mouseleave', this._onLeave);
      root.addEventListener('focusin', this._onEnter);
      root.addEventListener('focusout', this._onLeave);
    }

    // Rebuild when the original group's size changes (images loading, font
    // swaps, breakpoint crossings, container resize).
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(this._scheduleBuild);
      this.ro.observe(this.original);
      this.ro.observe(this.viewport);
    } else {
      window.addEventListener('resize', this._scheduleBuild);
    }

    REDUCED_MOTION.addEventListener('change', this._scheduleBuild);

    var self = this;
    waitForImages(this.original).then(function () {
      self.build();
    });
  }

  // The toggle is a child of the root, so focusing it bubbles a focusin here.
  // Pausing on that would make the Play button pause the marquee the instant it
  // receives focus, so hover/focus-pause ignores the control itself.
  Marquee.prototype._isToggleEvent = function (e) {
    return !!(e && e.target && this.toggle && this.toggle.contains(e.target));
  };

  Marquee.prototype._onEnter = function (e) {
    if (this._isToggleEvent(e)) return;
    this.hoverPaused = true;
    this._sync();
  };

  Marquee.prototype._onLeave = function (e) {
    if (this._isToggleEvent(e)) return;
    this.hoverPaused = false;
    this._sync();
  };

  // Which marquees this button drives. Only the explicit toggle goes global —
  // hover-pause stays local to the marquee under the pointer.
  Marquee.prototype._targets = function () {
    return this.controlAll ? liveInstances() : [this];
  };

  Marquee.prototype._onToggle = function () {
    var paused = !this._isPaused();
    this._targets().forEach(function (m) {
      m.userPaused = paused;
      m._sync();
    });
  };

  Marquee.prototype._isPaused = function () {
    return this.userPaused === null ? this.hoverPaused : this.userPaused;
  };

  // Single place that reconciles the animation and the control with the current
  // pause state, so a hover-pause can never leave the button showing "Pause"
  // over a stopped marquee. The accessible name carries the state ("Play
  // animation" once paused), so there is deliberately no aria-pressed to
  // double-announce it.
  Marquee.prototype._sync = function () {
    var paused = this._isPaused();

    if (this.toggle) {
      // The name has to describe what the control actually does, so it goes
      // plural once one button drives several marquees.
      var many = this.controlAll && this._targets().length > 1;
      var label = paused
        ? many ? 'Play all animations' : 'Play animation'
        : many ? 'Pause all animations' : 'Pause animation';
      this.toggle.classList.toggle('is-paused', paused);
      this.toggle.setAttribute('aria-label', label);
    }

    if (!this.animation) return;
    if (paused) this.animation.pause();
    else if (!REDUCED_MOTION.matches) this.animation.play();
  };

  Marquee.prototype._scheduleBuild = function () {
    var self = this;
    if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
    this.resizeRaf = requestAnimationFrame(function () {
      self.resizeRaf = 0;
      self.build();
    });
  };

  Marquee.prototype._clearClones = function () {
    var clones = this.track.querySelectorAll('.cuy-marquee__group[data-cuy-clone]');
    Array.prototype.forEach.call(clones, function (c) {
      c.parentNode.removeChild(c);
    });
  };

  // Clones are visual duplicates: hidden from the a11y tree (aria-hidden) and
  // skipped by keyboard tab order (tabindex=-1 on focusables), but their links
  // stay mouse-clickable — on an infinite marquee the tile under the cursor is
  // usually a clone, so clone anchors MUST navigate. We also strip form-field
  // and id identity so a clone can't (a) duplicate element ids or (b) join the
  // originals' radio groups — a same-name radio would otherwise steal :checked
  // from the visible product tile and drop its selected swatch.
  Marquee.prototype._sanitizeClone = function (clone) {
    var idNodes = clone.querySelectorAll('[id], [name], [for]');
    Array.prototype.forEach.call(idNodes, function (node) {
      node.removeAttribute('id');
      node.removeAttribute('name');
      node.removeAttribute('for');
      if (node.tagName === 'INPUT') node.checked = false;
    });
    var focusables = clone.querySelectorAll('a, button, input, select, textarea, [tabindex]');
    Array.prototype.forEach.call(focusables, function (node) {
      node.setAttribute('tabindex', '-1');
    });
  };

  Marquee.prototype.build = function () {
    if (!this.original.isConnected) return;

    var viewportWidth = this.viewport.getBoundingClientRect().width;
    var groupWidth = this.original.getBoundingClientRect().width;
    if (groupWidth < 1 || viewportWidth < 1) {
      // Genuinely hidden (e.g. the Visibility setting applies display:none at
      // this breakpoint) measures 0 forever — bail instead of spinning a rAF
      // loop. The ResizeObserver / resize handler re-runs build() when it's
      // shown again (both fire on the display:none -> visible transition).
      if (this.root.offsetParent === null) return;
      // Otherwise it's the transient "not measured yet" case; retry next frame.
      this._scheduleBuild();
      return;
    }

    // Skip needless rebuilds. The ResizeObserver also fires when lazysizes swaps
    // in images or product-tile/swatch JS mutates a tile; if neither the viewport
    // nor the content width actually changed, keep the current animation so a
    // pause (see _isPaused) isn't silently resumed by a fresh build.
    if (
      this.animation &&
      Math.abs(viewportWidth - this._lastVw) < 1 &&
      Math.abs(groupWidth - this._lastGw) < 1
    ) {
      return;
    }
    this._lastVw = viewportWidth;
    this._lastGw = groupWidth;

    if (this.animation) {
      this.animation.cancel();
      this.animation = null;
    }
    this._clearClones();

    if (REDUCED_MOTION.matches) {
      this.root.classList.add('cuy-marquee--ready');
      return;
    }

    // Enough groups that translating by one full group never reveals a gap.
    var groupsNeeded = Math.ceil(viewportWidth / groupWidth) + 1;
    if (groupsNeeded < 2) groupsNeeded = 2;

    for (var i = 1; i < groupsNeeded; i++) {
      var clone = this.original.cloneNode(true);
      this._sanitizeClone(clone);
      clone.setAttribute('data-cuy-clone', '');
      // Hidden from the a11y tree, but NOT inert — inert would also block mouse
      // clicks, and clone links must remain clickable (see _sanitizeClone).
      clone.setAttribute('aria-hidden', 'true');
      this.track.appendChild(clone);
    }

    // Distance from one group's start to the next includes the flex gap.
    var groups = this.track.querySelectorAll('.cuy-marquee__group');
    var distance =
      groups.length > 1
        ? groups[1].offsetLeft - groups[0].offsetLeft
        : groupWidth;
    if (distance < 1) distance = groupWidth;

    var duration = (distance / this.speed) * 1000; // ms for one loop

    var from = 'translateX(0px)';
    var to = 'translateX(' + -distance + 'px)';
    // Right-scroll is the same loop played from the -distance position toward 0.
    var frames =
      this.direction === 'right'
        ? [{ transform: to }, { transform: from }]
        : [{ transform: from }, { transform: to }];

    this.animation = this.track.animate(frames, {
      duration: duration,
      iterations: Infinity,
      easing: 'linear'
    });
    // Preserve an explicit or hover pause across rebuilds (resize, image load).
    if (this._isPaused()) this.animation.pause();

    this.root.classList.add('cuy-marquee--ready');
  };

  Marquee.prototype.destroy = function () {
    if (this.animation) this.animation.cancel();
    if (this.ro) this.ro.disconnect();
    else window.removeEventListener('resize', this._scheduleBuild);
    REDUCED_MOTION.removeEventListener('change', this._scheduleBuild);
    if (this.toggle) this.toggle.removeEventListener('click', this._onToggle);
    this.root.removeEventListener('mouseenter', this._onEnter);
    this.root.removeEventListener('mouseleave', this._onLeave);
    this.root.removeEventListener('focusin', this._onEnter);
    this.root.removeEventListener('focusout', this._onLeave);
    this._clearClones();
    this.root.classList.remove('cuy-marquee--ready');
    var i = INSTANCES.indexOf(this);
    if (i > -1) INSTANCES.splice(i, 1);
    delete this.root.__cuyMarquee;
  };

  function initWithin(scope) {
    var roots = (scope || document).querySelectorAll('.cuy-marquee');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__cuyMarquee) return;
      new Marquee(root);
    });
    applySingleButton();
  }

  function destroyWithin(scope) {
    var roots = (scope || document).querySelectorAll('.cuy-marquee');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__cuyMarquee) root.__cuyMarquee.destroy();
    });
    applySingleButton();
  }

  // A breakpoint change can hide or reveal a marquee (Visibility setting), which
  // changes which one should own the page's single button.
  var ownerRaf = 0;
  window.addEventListener('resize', function () {
    if (ownerRaf) cancelAnimationFrame(ownerRaf);
    ownerRaf = requestAnimationFrame(function () {
      ownerRaf = 0;
      applySingleButton();
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initWithin(document);
    });
  } else {
    initWithin(document);
  }

  // Shopify theme editor lifecycle.
  document.addEventListener('shopify:section:load', function (e) {
    initWithin(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    destroyWithin(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    initWithin(e.target.closest('.shopify-section') || document);
  });

  window.CuyMarquee = { init: initWithin, destroy: destroyWithin };
})();
